import { Placement } from "@floating-ui/react";
import { InteractiveContainer, Tooltip } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import {
  OnboardingFlowId,
  useOnboardingFlows,
} from "@web-src/modules/cx/providers/OnboardingFlowsProvider";
import React from "react";
import ghostImage from "./assets/ghost.png";

interface OnboardingFlowTooltipProps {
  flow: OnboardingFlowId;
  step: number;
  placement?: Placement;
  children: React.ReactNode;
}

export const OnboardingFlowTooltip: React.FC<OnboardingFlowTooltipProps> = ({
  flow,
  step,
  placement = "bottom-end",
  children,
}) => {
  const { t } = useTranslations();
  const { isStepActive, getStep, getTotalSteps, nextStep } =
    useOnboardingFlows();

  const isActive = isStepActive(flow, step);
  const stepData = getStep(flow, step);
  const totalSteps = getTotalSteps(flow);
  const isLastStep = step === totalSteps - 1;

  if (!stepData) return <>{children}</>;

  return (
    <Tooltip
      isOpen={isActive}
      placement={placement}
      zIndex={40}
      className="w-[360px] max-w-full rounded-[20px] bg-[#333333] p-0"
      arrowClassName="fill-[#333333]"
      renderTrigger={({ props, ref }) => (
        <div ref={ref} {...props}>
          {children}
        </div>
      )}
    >
      <div className="flex items-center justify-between gap-2 border-0 border-b border-solid border-dark-800 p-6 pb-0">
        <span className="font-secondary text-lg leading-[140%] font-semibold text-white">
          {stepData.title}
        </span>
        <img
          src={ghostImage}
          alt=""
          className="pointer-events-none relative h-[60px] translate-x-[25px] translate-y-[0.5px]"
        />
      </div>
      <div className="px-6 py-2.5">
        <span className="font-secondary text-sm text-[#E0E0E0]">
          {stepData.text}
        </span>
      </div>
      <div className="mx-6 mb-6 flex h-10 items-center justify-between px-2">
        {totalSteps > 1 ? (
          <span className="font-secondary text-xs text-[#DEDEE4]">
            {step + 1} / {totalSteps}
          </span>
        ) : (
          <div />
        )}
        <InteractiveContainer
          className="bg-[linear-gradient(149deg,#A9BDFF_68.46%,#F79FFF_93.9%)] bg-clip-text font-secondary text-sm leading-3 font-semibold text-transparent uppercase"
          onClick={nextStep}
        >
          {isLastStep
            ? t({ id: "common.got-it", defaultMessage: "Got it" })
            : t({ id: "common.next", defaultMessage: "Next" })}
        </InteractiveContainer>
      </div>
    </Tooltip>
  );
};
